'use strict';

/**
 * Merges a PortalApi graph DTO with local presentation metadata.
 * Live tracker/ticker must call this with the API payload — never a local bind-key catalog.
 */

import { PIPELINE_STATUS_GRAPH_PRESENTATION_BY_ID } from './pipelineStatusGraphPresentation';

/** @param {unknown} value */
function trimPart(value) {
	return value == null ? '' : String(value).trim();
}

/**
 * @param {unknown} apiGraph PortalApi `PipelineStatusGraphDto`
 * @returns {import('./pipelineStatusTypes').PipelineStatusGraphDefinition}
 */
export function composePipelineStatusLiveGraph(apiGraph) {
	if (apiGraph == null || typeof apiGraph !== 'object') {
		throw new Error(
			'[pipelineStatus] PortalApi snapshot is missing graph. Deploy PortalApi with the graph registry before this Aperture.',
		);
	}
	const raw = /** @type {Record<string, unknown>} */ (apiGraph);
	const id = trimPart(raw.id ?? raw.Id);
	const nodes = raw.nodes ?? raw.Nodes;
	if (!id) {
		throw new Error('[pipelineStatus] PortalApi graph is missing id.');
	}
	if (!Array.isArray(nodes) || nodes.length === 0) {
		throw new Error(`[pipelineStatus] PortalApi graph ${JSON.stringify(id)} has no nodes.`);
	}

	const stages = nodes.map((node, index) => {
		if (!node || typeof node !== 'object') {
			throw new Error(`[pipelineStatus] PortalApi graph ${JSON.stringify(id)} node at index ${index} is invalid.`);
		}
		const n = /** @type {Record<string, unknown>} */ (node);
		const nodeId = trimPart(n.id ?? n.Id);
		if (!nodeId) {
			throw new Error(`[pipelineStatus] PortalApi graph ${JSON.stringify(id)} node at index ${index} is missing id.`);
		}
		const presentation = PIPELINE_STATUS_GRAPH_PRESENTATION_BY_ID[nodeId];
		if (!presentation) {
			throw new Error(
				`[pipelineStatus] No presentation metadata for registry node ${JSON.stringify(nodeId)}.`,
			);
		}
		const factMatchRaw = n.factMatch ?? n.FactMatch;
		if (!factMatchRaw || typeof factMatchRaw !== 'object') {
			throw new Error(
				`[pipelineStatus] Registry node ${JSON.stringify(nodeId)} is missing factMatch.`,
			);
		}
		const fm = /** @type {Record<string, unknown>} */ (factMatchRaw);
		const service = trimPart(fm.service ?? fm.Service);
		const workflow = trimPart(fm.workflow ?? fm.Workflow);
		const pipeline = trimPart(fm.pipeline ?? fm.Pipeline);
		const stage = trimPart(fm.stage ?? fm.Stage);
		if (!service || !workflow || !pipeline || !stage) {
			throw new Error(
				`[pipelineStatus] Registry node ${JSON.stringify(nodeId)} factMatch is incomplete.`,
			);
		}
		const immRaw = n.iterationManifestMatch ?? n.IterationManifestMatch;
		const policyRaw = n.completionPolicy ?? n.CompletionPolicy;
		const iterationManifestMatch =
			immRaw && typeof immRaw === 'object'
				? {
						parentStage: trimPart(
							/** @type {Record<string, unknown>} */ (immRaw).parentStage
								?? /** @type {Record<string, unknown>} */ (immRaw).ParentStage,
						),
						parentService: trimPart(
							/** @type {Record<string, unknown>} */ (immRaw).parentService
								?? /** @type {Record<string, unknown>} */ (immRaw).ParentService,
						) || undefined,
						iterationDimension: trimPart(
							/** @type {Record<string, unknown>} */ (immRaw).iterationDimension
								?? /** @type {Record<string, unknown>} */ (immRaw).IterationDimension,
						),
					}
				: undefined;
		const completionPolicy =
			policyRaw != null && String(policyRaw).trim() !== '' ? String(policyRaw).trim() : undefined;

		return {
			id: nodeId,
			description: presentation.description,
			dependsOn: presentation.dependsOn,
			service,
			highLevelStage: presentation.highLevelStage,
			factMatch: {
				scope: presentation.scope,
				workflow,
				pipeline,
				stage,
				service,
			},
			...(iterationManifestMatch ? { iterationManifestMatch } : {}),
			...(completionPolicy ? { completionPolicy } : {}),
		};
	});

	return { id, stages };
}

/**
 * @param {unknown} catalog PortalApi `PipelineStatusGraphCatalogDto`
 * @returns {import('./pipelineStatusTypes').PipelineStatusGraphDefinition[]}
 */
export function composePipelineStatusLiveCatalog(catalog) {
	if (catalog == null || typeof catalog !== 'object') {
		throw new Error(
			'[pipelineStatus] PortalApi graph catalog is missing. Deploy PortalApi with GET /admin/pipeline-status/graphs before this Aperture.',
		);
	}
	const graphs = /** @type {Record<string, unknown>} */ (catalog).graphs
		?? /** @type {Record<string, unknown>} */ (catalog).Graphs;
	if (!Array.isArray(graphs) || graphs.length === 0) {
		throw new Error('[pipelineStatus] PortalApi graph catalog has no graphs.');
	}
	return graphs.map((graph) => composePipelineStatusLiveGraph(graph));
}
